import {
  Box, FormControl, FormHelperText, InputLabel, MenuItem, SelectChangeEvent, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ValidationFields } from '../../../@types/Account';
import { SubdomainTextField } from '../../../components/common/textFields/SubdomainTextField';
import STRING_KEYS from '../../../language/keys';
import CopyableLabel from '../../../components/CopyableLabel';
import { ACCOUNT_STATUSES } from '../../../consts';
import CustomTextField from '../../../components/CustomTextField';
import { AdminLoginButton } from '../AdminLoginButton';
import { useAuth } from '../../../hooks/useAuth';
import { VIEW_MODE } from '../../../consts/enums';
import { PERMISSIONS } from '../../../consts/permissions';
import CustomSelect from '../../../components/CustomSelect';
import RecruitingTypeSelect from '../../../components/RecruitingTypeSelect';
import { style } from './style';

type AccountInformationSectionProps = {
  displayNameFields: ValidationFields;
  accountStatusFields: ValidationFields;
  subDomainNameValues: ValidationFields;
  recruitingUsageTypeFields: ValidationFields;
  onValuesChange: (name: string, value: string) => void;
  accountId: string;
  onLoginAsAdminClick: () => void;
  viewMode: ViewMode;
};

export const AccountInformationSection = ({
  displayNameFields,
  accountStatusFields,
  subDomainNameValues,
  recruitingUsageTypeFields,
  onValuesChange,
  accountId,
  onLoginAsAdminClick,
  viewMode,
}: AccountInformationSectionProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();
  const isReadOnly = viewMode === VIEW_MODE.READ_ONLY;

  const renderStatatusesMenuItem = () => {
    return ACCOUNT_STATUSES.map(({ value, text }) => {
      return (
        <MenuItem key={ value } value={ value }>
          {text}
        </MenuItem>
      );
    });
  };

  return (
    <Box>
      <Box sx={ style.card.row.sx }>
        <Typography variant="h6" sx={ style.card.row.Typography }>
          {translate(STRING_KEYS.ACCOUNT_LOGIN_BTN_TEXT)}
        </Typography>

        {hasPermissions([PERMISSIONS.ACCOUNT_LOGIN_AS_ADMIN]) && (
          <AdminLoginButton
            text={ translate(STRING_KEYS.ACCOUNTS_ACTION_MENU_LOGIN_AS_ADMIN) }
            loginAsAdminClick={ onLoginAsAdminClick }
          />
        )}
      </Box>
      <CustomTextField
        viewMode={ viewMode }
        label={ translate(STRING_KEYS.ACCOUNTS_NAME) }
        variant="outlined"
        value={ displayNameFields.value }
        error={ displayNameFields?.error }
        name="displayName"
        InputLabelProps={ style.label }
        onChange={ (e) => {
          onValuesChange('displayName', e.target.value);
        } }
        helperText={
          displayNameFields?.error ? displayNameFields.helperText : ''
        }
        margin="normal"
        fullWidth
      />
      <SubdomainTextField
        onChange={ onValuesChange }
        subDomainFields={ subDomainNameValues }
        viewMode={ viewMode }
      />
      <Box sx={ style.card.row.sx }>
        <RecruitingTypeSelect
          value={ recruitingUsageTypeFields.value }
          onChange={ (value: string) => onValuesChange('recruitingUsageType', value) }
          error={ recruitingUsageTypeFields?.error }
          helperText={ recruitingUsageTypeFields?.error ? recruitingUsageTypeFields.helperText.toString() : '' }
          viewMode={ viewMode }
        />
      </Box>
      <Box sx={ style.card.row.sx }>
        <FormControl
          fullWidth
          error={ accountStatusFields?.error }
          variant={ isReadOnly ? 'standard' : 'outlined' }
          margin="normal"
        >
          <InputLabel id="Status">Status</InputLabel>
          <CustomSelect
            label="Status"
            labelId="Status"
            value={ accountStatusFields.value }
            error={ accountStatusFields?.error }
            name="accountStatus"
            viewMode={ viewMode }
            fullWidth
            onChange={ (e: SelectChangeEvent<unknown>) => {
              onValuesChange('accountStatus', e.target.value as string);
            } }
          >
            {renderStatatusesMenuItem()}
          </CustomSelect>
          <FormHelperText>
            {accountStatusFields?.error ? accountStatusFields.helperText : ''}
          </FormHelperText>
        </FormControl>
        <Box>
          <Box sx={ style.card.copybleBlock.sx }>ID</Box>
          <CopyableLabel
            labelText={ accountId }
            dataToCopy={ accountId }
            variant="caption"
            maxWidth="10rem"
            textColor="text.primary"
          />
        </Box>
      </Box>
    </Box>
  );
};
